import type { TFunction } from "i18next";

type UserLike =
  | {
      displayName?: string | null;
      username?: string | null;
    }
  | null
  | undefined;

export type UserDisplayLabel = {
  label: string;
  isFallback: boolean;
};

/**
 * Resolve a user-facing label for a user record. Returns:
 *   - the trimmed displayName if present
 *   - else "@username" if the user has only a username
 *   - else the localized "Anonymous" placeholder, flagged as isFallback=true
 *
 * Callers should branch on `isFallback` to apply a visually distinct style
 * (typically italic) so it's clear the user hasn't set a name yet.
 */
export const getUserDisplayLabel = (
  user: UserLike,
  t: TFunction,
): UserDisplayLabel => {
  const displayName = user?.displayName?.trim();
  if (displayName) {
    return { label: displayName, isFallback: false };
  }
  const username = user?.username?.trim();
  if (username) {
    return { label: `@${username}`, isFallback: false };
  }
  return { label: t("ANONYMOUS_USER"), isFallback: true };
};
